<template>
  <div>
    <!-- loading -->
    <div v-if="status === 'loading'" class="text-xs-center">
      <v-progress-circular class="my-3" indeterminate color="accent" :size="50"></v-progress-circular>
    </div>

    <!-- error -->
    <div v-if="status === 'error'" class="text-xs-center">
      <p class="grey--text">{{ $t('Common.genericErrorMsg') }}</p>
      <v-btn depressed color="accent" round @click="tryAgain">
        <v-icon left>refresh</v-icon>
        {{ $t('Common.tryAgain') }}
      </v-btn>
    </div>

    <!-- success -->
    <div v-if="status === 'success' && Object.keys(companyConfig).length">
      <form class="form--custom" novalidate @submit.prevent="handleSubmit">
        <v-radio-group v-model="radioGroup" class="pt-1 mt-0">
          <v-radio :label="$t(`${namespace}.rationaleForRunningRadioOptional`)" value="optional" />
          <v-radio :label="$t(`${namespace}.rationaleForRunningRadioDefault`)" value="default" />
          <v-radio :label="$t(`${namespace}.rationaleForRunningRadioManual`)" value="manual" />
        </v-radio-group>

        <v-layout style="padding-left: 32px;" v-if="radioGroup === 'manual'">
          <v-flex>
            <div class="pr-2">
              <label>{{ $t(`${namespace}.rationaleForRunningMinChars`) }}</label>
              <v-text-field
                v-model="config.minNoteLength"
                :readonly="radioGroup !== 'manual'"
                required
                flat
                outline
                solo
                color="accent"
                type="number"
                :placeholder="$t(`${namespace}.rationaleForRunningPlaceholderMinChars`)"
                :error-messages="errors.first($t(`${namespace}.rationaleForRunningLabelMinChars`))"
                v-validate="{ required: true, min_value: 1, max_value: config.maxNoteLength }"
                :data-vv-name="$t(`${namespace}.rationaleForRunningLabelMinChars`)"
              />
            </div>
          </v-flex>

          <v-flex>
            <div class="pl-2">
              <label>{{ $t(`${namespace}.rationaleForRunningMaxChars`) }}</label>
              <v-text-field
                v-model="config.maxNoteLength"
                :readonly="radioGroup !== 'manual'"
                required
                flat
                outline
                solo
                color="accent"
                type="number"
                :placeholder="$t(`${namespace}.rationaleForRunningPlaceholderMaxChars`)"
                :error-messages="errors.first($t(`${namespace}.rationaleForRunningLabelMaxChars`))"
                v-validate="{ required: true, min_value: config.minNoteLength }"
                :data-vv-name="$t(`${namespace}.rationaleForRunningLabelMaxChars`)"
              />
            </div>
          </v-flex>
        </v-layout>

        <v-layout>
          <v-spacer></v-spacer>
          <v-btn
            :disabled="isSaving"
            class="text--normal"
            color="accent"
            flat
            round
            @click="close"
          >
            {{ $t('Common.cancel') }}
          </v-btn>

          <v-btn
            type="submit"
            round
            depressed
            color="accent"
            :disabled="isSaving"
            :loading="isSaving"
          >
            {{ $t('Common.save') }}
          </v-btn>
        </v-layout>

        <div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'RationaleForRunning',
  props: {
    companyConfig: {
      type: Object,
      default: () => ({
        minNoteLength: 4,
        maxNoteLength: 1024
      })
    },
    status: {
      type: String,
      default: 'loading'
    }
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    routeId () {
      if (this.$route.params.id) {
        return Number(this.$route.params.id)
      }

      if (this.company) {
        return this.company.id
      }

      return null
    }
  },
  data () {
    return {
      namespace: 'Views.UsagePoliciesPage',
      radioGroup: 'default',
      config: {},
      isSaving: false
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    tryAgain () {
      this.$emit('tryAgain')
    },

    close () {
      this.$emit('close')
    },

    setConfigValues () {
      if (!this.companyConfig || !Object.keys(this.companyConfig).length) return

      if (this.companyConfig.minNoteLength === 0 && this.companyConfig.maxNoteLength === 1024) {
        this.radioGroup = 'optional'
      } else if (this.companyConfig.minNoteLength === 4 && this.companyConfig.maxNoteLength === 1024) {
        this.radioGroup = 'default'
      } else {
        this.radioGroup = 'manual'
        this.config.minNoteLength = this.companyConfig.minNoteLength
        this.config.maxNoteLength = this.companyConfig.maxNoteLength
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        if (!isValid) return

        this.isSaving = true

        let minNoteLength = null
        let maxNoteLength = null

        if (this.radioGroup === 'optional') {
          this.config.showNote = false
          minNoteLength = 0
          maxNoteLength = 1024
        } else if (this.radioGroup === 'default') {
          this.config.showNote = true
          minNoteLength = 4
          maxNoteLength = 1024
        } else {
          this.config.showNote = true
          minNoteLength = this.config.minNoteLength
          maxNoteLength = this.config.maxNoteLength
        }

        await this.$apiService.setCompanyConfig(
          this.routeId,
          { showNote: this.config.showNote },
          { minNoteLength },
          { maxNoteLength },
          { companyId: this.routeId }
        )

        this.$emit('success')
        this.successAlert(this.$t(`${this.namespace}.rationaleForRunningSuccess`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.rationaleForRunningError`))
      }

      this.isSaving = false
    }
  },
  watch: {
    status (val) {
      if (val === 'success') {
        this.setConfigValues()
      }
    }
  },
  created () {
    this.setConfigValues()
  }
}
</script>
